/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import $ from 'jquery';
import { types as repr, types as coreTypes } from '@sitebuilder/core';
import {
    hooks as commonHooks,
    analytics as commonAnalytics,
    annotations as commonAnnotations,
    types as commonTypes,
    components as commonComponents,
    utils as commonUtils,
} from '@sitebuilder/common';

import { sortBy } from 'lodash';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import RunningFooter from './RunningFooter';
import { Iframe } from './iFrameResize';
import rootDocumentStyles from './RootDocument.styles';
import { applyAdditionalCss } from './utils';

const processAnnotations = (
    existingAnnotations: commonComponents.AppContext.Annotations,
    annotationsArr: commonComponents.AppContext.Annotation[]
) => {
    const annotationsIds = existingAnnotations.annotationsIds;

    if (Array.isArray(annotationsArr)) {
        for (const a of annotationsArr) {
            const idParts = a?.id?.split('-');
            a._id = idParts[idParts.length - 1];
        }
    }

    if (
        Array.isArray(annotationsArr) &&
        Array.isArray(annotationsIds) &&
        annotationsIds.length > 0
    ) {
        annotationsArr = annotationsIds.length
            ? sortBy(annotationsArr, (a) => {
                  return annotationsIds.indexOf(a._id || '') !== -1
                      ? annotationsIds.indexOf(a._id || '')
                      : annotationsArr.length;
              })
            : annotationsArr;
    }

    return {
        items: annotationsArr,
        markerCount: 0,
        markerGroupCount: {},
    };
};

interface DynamicEvent {
    eventName: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}

export interface WidgetProperties
    extends commonTypes.WidgetPropertiesBase,
        commonTypes.RootDocumentWidgetPropertiesBase,
        repr.PDFOptions,
        repr.MetaDataOptions,
        repr.CacheControl,
        repr.PptxOptions {
    fonts: { name: string; url: string; format: string }[];
    resourceStrings: repr.Resource;
    themes?: {
        id: string;
        conditionFunction: string;
        themeStyles: string;
        colorPallete: string[];
    }[];
    dataSource: string;
    dataType: 'none' | 'webProfile' | 'fundList';
    documentType: 'web' | 'pdf' | 'pptx';
    annotationsMarkerHtml: string;
    annotationsTextHtml: string;
    annotationsTextGroupHtml: string;
    sortAnnotationsBy: string;

    // TODO: this is hard-coded to two locales!
    // there are other metadata that we want to have other than title
    // and want to also be able to set response haders, redirect, rewrite, etc.
    // i.e. custom middleware, this could be site level.
    pageTitleEn: string;
    pageTitleFr: string;
    loadingDataText: string;
    isWithinIFrame: boolean;
    iframeStickyHeaderType: string;
    stickyHeaderWrapperId: string;
    stickyHeaderClass: string;
    addScripts: boolean;
    scriptsHead: string[];
    scriptsBody: string[];
    cachingHeader: string;
    cspHeader: string;
    theme: string;
    colorSpace: coreTypes.ColorScheme;
    isDynamicTheme: boolean;
    functionToSelectTheme: string;
    typographyClass?: string;
    functionToTransform?: string;
    isTransformData?: boolean;
    usePageAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
    includeAllProfiles: boolean;
    includeHistoricalHoldings: boolean;
    includeUnderlyingFundPerformance: boolean;
    includeFundSeriesPerformance: boolean;
    includeAnnotations: boolean;
    annotationFilterProperty: string;
}

const RootDocument: FunctionComponent<
    React.PropsWithChildren<commonTypes.WidgetInstanceProps>
> = (): ReactElement => {
    const appContext = commonHooks.useAppContext();
    const { Placeholder, logger } = appContext;
    const i18n = commonHooks.useLocalization();
    const {
        data,
        documentMetadata,
        themeSelected: selectedThemeName,
        localeObject: availableLocale,
        inEditMode,
        pageName,
        componentName,
    } = appContext;
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>('root') || {};

    const {
        fonts,
        dataSource,
        dataType,
        styles,
        documentType,
        pdfPageSize,
        pdfPageMargin,
        resourceStrings,
        pageTitleEn,
        pageTitleFr,
        loadingDataText,
        themes,
        annotationsMarkerHtml,
        annotationsTextHtml,
        annotationsTextGroupHtml,
        sortAnnotationsBy,
        usePageAnalytics,
        dynamicEvents,
        ...rest
    } = widgetProperties || {};

    const [localeObject, setLocaleObject] = useState<Locale | null>(availableLocale || null);
    const [selectedList, setSelectedList] = useState<string[]>([]);
    const [annotations, setAnnotations] = useState<commonComponents.AppContext.Annotations>(
        appContext.annotations || {
            items: [],
            markerCount: 0,
            markerGroupCount: {},
            formats: {},
        }
    );
    const [queries, setQueries] = useState<commonComponents.AppContext.Queries>(
        appContext.queries || {}
    );
    const location = useLocation();
    const pathName = location.pathname;
    const search = location.search;
    const params = new URLSearchParams(search);
    let cssStyles = '';

    const PDFOptions: repr.PDFOptions = {
        pdfPageMargin,
        pdfPageSize,
        pageSize: rest.pageSize,
        pageWidth: rest.pageWidth,
        pageHeight: rest.pageHeight,
        pageOrientation: rest.pageOrientation,
        pageMargin: rest.pageMargin,
        pageBleed: rest.pageBleed,
        addPdfTags: rest.addPdfTags,
        isRunningHeader: rest.isRunningHeader,
        isRunningFooter: rest.isRunningFooter,
    };

    const PptxOptions: repr.PptxOptions = {
        pptxPageSize: rest.pptxPageSize,
        pptxPageWidth: rest.pptxPageWidth,
        pptxPageHeight: rest.pptxPageHeight,
        pptxPageOrientation: rest.pptxPageOrientation,
        pptxPageMargin: rest.pptxPageMargin,
        pptxPageBleed: rest.pptxPageBleed,
        pptxIsRunningHeader: rest?.pptxIsRunningHeader,
        pptxIsRunningFooter: rest?.pptxIsRunningFooter,
    };

    const locale: string = documentMetadata.locale;
    const lang: 'en' | 'fr' = locale.substr(0, 2) as 'en' | 'fr';

    const newRootDocumentData = {
        search,
        resourceStrings: {
            ...resourceStrings?.[locale],
            ...data.siteResources?.[locale],
        },
        queryStringParams: params.toString(),
        lang,
        colorPallete: [] as string[],
        ...data,
    };

    const analytics = commonAnalytics.useAnalyticsTracking();

    logger.debug('RERENDER RootDocument');

    useEffect(() => {
        if (usePageAnalytics) {
            const updatedDynamicEvents = dynamicEvents?.map((dynamicEvent: DynamicEvent) => {
                const { analyticItems } = dynamicEvent || {};
                if (analyticItems && analyticItems.length > 0) {
                    const updatedAnalyticItems = analyticItems.map((item: AnalyticsItem) => {
                        // Modify the value property and return a new AnalyticsItem object
                        return {
                            ...item,
                            value: i18n.format(item.value, newRootDocumentData),
                        };
                    });
                    // Return a new dynamicEvent object with updated analyticItems
                    return {
                        ...dynamicEvent,
                        analyticItems: updatedAnalyticItems,
                    };
                } else {
                    return dynamicEvent;
                }
            });
            const trackingData = {
                dynamicEvents: updatedDynamicEvents,
            };
            analytics.track(trackingData);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, usePageAnalytics, dynamicEvents]);

    useEffect(() => {
        import('date-fns/locale').then((supportedLocales) => {
            const supportedLocale =
                (supportedLocales as any)[locale] ||
                (supportedLocales as any)[locale.replace('-', '')];
            setLocaleObject(supportedLocale);
        });
        // eslint-disable-next-line
    }, []);

    const addScriptToDocument = (script: string, location: 'head' | 'body') => {
        const encodeScriptBinded = i18n.format(script, newRootDocumentData) ?? script;
        const decodedScriptString = encodeScriptBinded
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&#x3D;/g, '=')
            .replace(/&quot;/g, '"');

        const pattern = /<script([^>]*)src="([^"]+)"([^>]*)>/;
        const matchedValue = decodedScriptString.match(pattern);

        if (matchedValue) {
            const srcValue = matchedValue[2];
            const otherAttributes = matchedValue[1] + matchedValue[3];

            const existingScripts = document.querySelectorAll('script');
            const isAlreadyExists = Array.from(existingScripts).some(
                (scriptElement) => scriptElement.src === srcValue
            );

            if (!isAlreadyExists) {
                const scriptElement = document.createElement('script');
                scriptElement.src = srcValue;

                // Parse other attributes
                const attributes = otherAttributes.match(/(\S+)=["']([^"']+)["']/g);
                if (attributes) {
                    attributes.forEach((attr) => {
                        const [name, value] = attr.split('=');
                        scriptElement.setAttribute(name, value.replace(/["']/g, ''));
                    });
                }

                if (location === 'head') {
                    document.head.appendChild(scriptElement);
                } else {
                    document.body.appendChild(scriptElement);
                }
            }
        }
    };

    useEffect(() => {
        if (documentType === 'web') {
            if (rest?.addScripts) {
                rest?.scriptsBody?.forEach((script: string) => addScriptToDocument(script, 'body'));
                rest?.scriptsHead?.forEach((script: string) => addScriptToDocument(script, 'head'));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // check if documenttype is web
        if (!rest.isWithinIFrame || documentType !== 'web') return;

        const checkNavLink = setInterval(() => {
            // Check if at least one of the elements exists
            const isElementRendered =
                $('.nav-link').length > 0 ||
                $('.nav-item').length > 0 ||
                $('#navbar').length > 0 ||
                $('.nav-items a').length > 0 ||
                $(`#${rest.stickyHeaderWrapperId}`).length > 0 ||
                $(`.${rest.stickyHeaderClass}`).length > 0;

            // Initializing class after DOM has element to avoid server side render
            if (isElementRendered) {
                const iframe = new Iframe(
                    rest.iframeStickyHeaderType,
                    rest.stickyHeaderWrapperId,
                    rest.stickyHeaderClass,
                    pageName,
                    componentName,
                    logger
                );

                let debounceTimer: any = 0;
                const onGetMessage = (e: any) => {
                    // if there's a pending call, cancel it
                    clearTimeout(debounceTimer);
                    // schedule a call after 10ms
                    debounceTimer = setTimeout(() => {
                        iframe.makeUpdates(e);
                    }, 100);
                };

                // Listens to event from parent window
                window.addEventListener('message', onGetMessage);

                // Clear interval since navbarElem is found
                clearInterval(checkNavLink);

                return () => {
                    // Remove event listener
                    window.removeEventListener('message', onGetMessage);
                };
            }
        }, 100); // Checks every 100ms

        // Cleanup interval on unmount
        return () => clearInterval(checkNavLink);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest.isWithinIFrame]);

    useEffect(() => {
        const title = documentMetadata?.pageTitle;

        if (title && typeof window !== 'undefined') {
            document.title = title;
        }
    }, [documentMetadata]);

    const annotationsArr = data?.annotations as commonComponents.AppContext.Annotation[];

    commonHooks.useSSE(async () => {
        if (Array.isArray(annotationsArr) && annotationsArr.length > 0) {
            const annotationsIds = sortAnnotationsBy ? sortAnnotationsBy.split(',') : [];
            const newAnnotations: commonComponents.AppContext.Annotation[] = annotationsArr.map(
                (a) => ({
                    ...a,
                    sortOrder:
                        annotationsIds.indexOf(a._id || '') !== -1
                            ? annotationsIds.indexOf(a._id || '')
                            : annotationsArr.length,
                })
            );

            const annotationIds = annotations.items.map((a) => a.id);
            const newAnnotationIds = newAnnotations.map((a) => a.id);
            const newAnnotationId = newAnnotationIds.find((id) => !annotationIds.includes(id));

            if (!newAnnotationId) return;

            const processedAnnotations = processAnnotations(annotations, newAnnotations);
            setAnnotations({ ...annotations, ...processedAnnotations });
            logger.debug('annotations updated', { annotations: processedAnnotations });
        }
    }, [annotationsArr, sortAnnotationsBy]);

    const applyFormatsToAnnotations = (annotations: commonComponents.AppContext.Annotations) => {
        const urlPathAndPrefix = pathName + search;
        const formats = {
            marker:
                i18n.format(annotationsMarkerHtml, newRootDocumentData) ||
                // eslint-disable-next-line max-len
                `<a id="{markerId}" aria-label="${i18n.t(
                    'marker_id'
                )} {marker}" class="ann-marker" href="${urlPathAndPrefix}#{textId}">{marker}</a>`,
            text:
                i18n.format(annotationsTextHtml, newRootDocumentData) ||
                '<li id="{textId}">' +
                    // eslint-disable-next-line max-len
                    `<sup><a class="ann-marker" aria-label="${i18n.t(
                        'marker_id'
                    )} {marker}" href="${urlPathAndPrefix}#{markerId}">{marker}</a></sup>` +
                    '&nbsp;{text}</li>',
            textGroup:
                i18n.format(annotationsTextGroupHtml, newRootDocumentData) ||
                '<ul class="ann-list">{items}</ul>',
        };

        return { ...annotations, formats };
    };

    const fontsCss =
        fonts && fonts.length
            ? fonts.map(
                  (f) =>
                      `@font-face {
                        font-family: '${f.name}';
                        src: url(${f.url}) format('${f.format}');
                        font-style: normal;
                    }`
              )
            : [];

    cssStyles += fontsCss.join('\n');

    if (documentType === 'pdf') {
        cssStyles += applyAdditionalCss(
            documentType,
            {
                size: PDFOptions.pageSize,
                orientation: PDFOptions.pageOrientation,
                width: PDFOptions.pageWidth,
                height: PDFOptions.pageHeight,
                margin: PDFOptions.pageMargin,
                bleed: PDFOptions.pageBleed,
                isRunningHeader: PDFOptions.isRunningHeader,
                isRunningFooter: PDFOptions.isRunningFooter,
            },
            { pdfPageSize, pdfPageMargin },
            { originalSize: 'letter portrait', originalMargin: '0.4in' }
        );
    } else if (documentType === 'pptx') {
        cssStyles += applyAdditionalCss(documentType, {
            size: PptxOptions.pptxPageSize,
            orientation: PptxOptions.pptxPageOrientation,
            width: PptxOptions.pptxPageWidth,
            height: PptxOptions.pptxPageHeight,
            margin: PptxOptions.pptxPageMargin,
            bleed: PptxOptions.pptxPageBleed,
            isRunningHeader: PDFOptions.isRunningHeader,
            isRunningFooter: PDFOptions.isRunningFooter,
        });
    }

    let theme = '';
    const colorSpace = rest.colorSpace || (documentType === 'web' ? 'rgb' : 'cmyk');

    // todo: this implies the collection of all themes should be available
    // in the data in order to apply the correct theme,
    // Apply themes
    // as an alternative, we could fetch the required theme on the server side
    // when fetching initial data
    if (themes) {
        themes.forEach((t) => {
            if (t.id && t.conditionFunction && t.themeStyles) {
                try {
                    // eslint-disable-next-line no-new-func
                    const shouldApplyTheme = commonUtils.transformDataWithGlobals(
                        `root-${t.id}-transform`,
                        t.conditionFunction,
                        newRootDocumentData
                    );

                    if (shouldApplyTheme) {
                        logger.debug('applying theme', { t });
                        // todo: verify intended behavior is to append theme styles
                        // but replace entire color pallete
                        theme += t.themeStyles;
                        newRootDocumentData.colorPallete = t.colorPallete;
                    }
                } catch (err) {
                    logger.error(err);
                }
            }
        });
    }

    const processedAnnotations = applyFormatsToAnnotations(annotations);

    return (
        <commonComponents.AppContext.AppContextProvider
            {...{
                ...appContext,
                fundList: { selectedList },
                annotations: processedAnnotations,
                queries: queries,
                setters: { setSelectedList, setAnnotations, setQueries },
                annotator: new commonAnnotations.annotator.Annotator(processedAnnotations),
                localeObject: localeObject || undefined,
                documentType,
            }}
        >
            <div
                css={css`
                    ${cssStyles || ''}${theme || ''}${rootDocumentStyles}${getTextSchemaProperties(
                        widgetProperties,
                        colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles || ''}
                `}
                className={`${locale === 'fr-CA' ? 'french' : ''} ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
            >
                {rest?.isRunningHeader || rest?.pptxIsRunningHeader ? (
                    <div className="running-header">
                        <Placeholder
                            contentPlaceHolderId={'running-header'}
                            parentWidgetInstanceId={'root'}
                            data={data}
                        />
                        {inEditMode && (
                            <div className="running-header-edit-mode">
                                <div className="border" />
                                <span>Add running header components above this</span>
                            </div>
                        )}
                    </div>
                ) : null}
                {!inEditMode && <RunningFooter rest={rest} data={data} />}
                <Placeholder
                    data={{
                        ...newRootDocumentData,
                        theme,
                        themeSelected: selectedThemeName,
                        inEditMode,
                        colorSpace,
                    }}
                    contentPlaceHolderId="children"
                    parentWidgetInstanceId="root"
                />
                {inEditMode && <RunningFooter rest={rest} data={data} />}
            </div>
        </commonComponents.AppContext.AppContextProvider>
    );
};

export default RootDocument;
